import { z } from 'zod';

/**
 * Suporte ticket system — shared Zod schemas (Phase 33 plan 02).
 *
 * Single source of truth for ticket shapes consumed by:
 *   - Backend NestJS DTOs (via createZodDto / nestjs-zod)
 *   - Frontend api-client typed responses
 *
 * Framework-agnostic: does NOT import @prisma/client — enum literals duplicated explicitly.
 *
 * Enums mirror Prisma schema:
 *   SuporteTicketStatus: aberto | resolvido
 *   SuporteCategoria:    pagamento | cadastro | evento | outros
 */

// ---------- Enums ----------

export const SuporteTicketStatusSchema = z.enum(['aberto', 'resolvido']);
export type SuporteTicketStatus = z.infer<typeof SuporteTicketStatusSchema>;

export const SuporteCategoriaSchema = z.enum(['pagamento', 'cadastro', 'evento', 'outros']);
export type SuporteCategoria = z.infer<typeof SuporteCategoriaSchema>;

// ---------- Input schemas ----------

export const CriarTicketInputSchema = z.object({
  assunto: z.string().min(1, 'Informe o assunto do ticket').max(120),
  categoria: SuporteCategoriaSchema,
  descricao: z.string().min(1, 'Descreva o problema').max(2000),
});
export type CriarTicketInput = z.infer<typeof CriarTicketInputSchema>;

export const ResponderTicketInputSchema = z.object({
  mensagem: z.string().min(1).max(2000),
});
export type ResponderTicketInput = z.infer<typeof ResponderTicketInputSchema>;

// ---------- DTO shapes ----------

export const MensagemDtoSchema = z.object({
  id: z.string(),
  ticketId: z.string(),
  autorUserId: z.string().nullable(),
  isFounder: z.boolean(),
  mensagem: z.string(),
  lidoEm: z.string().nullable(), // ISO 8601 or null
  criadoEm: z.string(),          // ISO 8601
});
export type MensagemDto = z.infer<typeof MensagemDtoSchema>;

export const TicketDtoSchema = z.object({
  id: z.string(),
  userId: z.string(),
  assunto: z.string(),
  categoria: SuporteCategoriaSchema,
  descricao: z.string(),
  status: SuporteTicketStatusSchema,
  criadoEm: z.string(),           // ISO 8601
  ultimaAtividadeEm: z.string(),  // ISO 8601
  hasUnreadReply: z.boolean(),    // true when any isFounder message has lidoEm=null
  autorNome: z.string().optional(), // admin list context only; absent on user-facing lists
});
export type TicketDto = z.infer<typeof TicketDtoSchema>;

export const TicketDetalheDtoSchema = TicketDtoSchema.extend({
  mensagens: z.array(MensagemDtoSchema),
});
export type TicketDetalheDto = z.infer<typeof TicketDetalheDtoSchema>;

// ---------- Page response (cursor pagination) ----------

export const TicketPageSchema = z.object({
  items: z.array(TicketDtoSchema),
  nextCursor: z.string().nullable(),
});
export type TicketPage = z.infer<typeof TicketPageSchema>;

// ---------- Unread count ----------

export const UnreadCountResponseSchema = z.object({
  unreadCount: z.number().int().min(0),
});
export type UnreadCountResponse = z.infer<typeof UnreadCountResponseSchema>;

// ---------- Query schemas ----------

export const UserTicketQuerySchema = z.object({
  status: SuporteTicketStatusSchema.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type UserTicketQuery = z.infer<typeof UserTicketQuerySchema>;

export const AdminTicketQuerySchema = z.object({
  status: SuporteTicketStatusSchema.optional(),
  categoria: SuporteCategoriaSchema.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type AdminTicketQuery = z.infer<typeof AdminTicketQuerySchema>;
